import * as React from 'react'
import { useContext } from 'react'
import { Button, Col, Row } from 'react-bootstrap'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import step1_schema from './step1.schema'
import * as FormWizardStyles from '../../../../components/organisms/FormWizard/styles'
import { FormWizardHandlers } from '../../../../components/organisms/FormWizard'
import { CreateContractsContext } from '../index'
import { CPFAndCNPJTagInput } from '../../../../components/molecules/CPFAndCNPJTagInput'
import { useContextPage } from '../contextPage'
import { Contract } from '../../../../domain/usecases/Contracts'
import { useToast } from '../../../../hooks/useToast'

export type ICreateContractsFormStep1 = Pick<Contract.CreateParams, 'buyers'>

export default function Step1({
  nextHandler,
  backHandler
}: FormWizardHandlers) {
  const { data, setData } = useContext(CreateContractsContext)
  const { showToast } = useToast()
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors }
  } = useForm<ICreateContractsFormStep1>({
    resolver: yupResolver(step1_schema),
    defaultValues: {
      buyers: data?.buyers || []
      // ...data
    }
  })
  const { state, setState, getPersons } = useContextPage()
  const [promissoryBuyerCPForCNPJ, setPromissoryBuyerCPForCNPJ] =
    React.useState<string[]>([])

  React.useEffect(() => {
    if (data?.cpfOrCnpjList?.length) {
      setPromissoryBuyerCPForCNPJ(data.cpfOrCnpjList)
    }
  }, [data?.cpfOrCnpjList])

  const onClickNext = (formData: ICreateContractsFormStep1) => {
    if (isInvalidToNextStep())
      return showToast(
        'Todos os Promissários precisam estar cadastrados.',
        'error'
      )
    const buyersIds = state.persons?.map((person) => person.id)
    setData?.({ ...data, buyers: buyersIds })
    nextHandler()
  }

  // Também barra quando voltou menos pessoas que CPFs/CNPJs digitados
  // (RemoteGetPersons descarta os que falharam; e a busca é assíncrona).
  const isInvalidToNextStep = () => {
    const persons = state?.persons ?? []
    return (
      persons.length !== promissoryBuyerCPForCNPJ.length ||
      !persons.every((person) => person.id)
    )
  }

  React.useEffect(() => {
    const tryGetPersons = async () => {
      if (!promissoryBuyerCPForCNPJ || promissoryBuyerCPForCNPJ.length === 0) {
        setState((prevState) => ({
          ...prevState,
          persons: [],
          cpfOrCnpjList: []
        }))
        return
      }

      try {
        const persons = await getPersons.getAll({
          cpfOrCnpj: promissoryBuyerCPForCNPJ
        })

        const validPersons = persons.filter(
          (person: any) => person && typeof person === 'object' && person.info
        )

        setState((prevState) => {
          return {
            ...prevState,
            persons: validPersons,
            cpfOrCnpjList: promissoryBuyerCPForCNPJ
          }
        })
      } catch (error) {
        showToast(
          'Nao foi possivel buscar informações sobre os CPFs/CNPJs informados.',
          'error'
        )
      }
    }

    tryGetPersons()
  }, [promissoryBuyerCPForCNPJ])

  return (
    <div className="m-3">
      <FormWizardStyles.TabContent>
        <Row className="mt-4">
          <Col>
            <Controller
              control={control}
              name="buyers"
              render={({ field: { onChange, value } }) => {
                const defaultValues = promissoryBuyerCPForCNPJ?.map(
                  (cpfOrCnpj, index) => ({
                    id: `${Date.now()}-${index}`,
                    name: cpfOrCnpj
                  })
                )
                return (
                  <CPFAndCNPJTagInput
                    key={promissoryBuyerCPForCNPJ?.join(',') || 'empty'}
                    defaultValue={defaultValues}
                    label="Digite o(s) CPF(s)/CNPJ(s) do(s) Promissários(s)"
                    onChange={(event) => {
                      // O TagInput remonta (key) e reemite a mesma lista; sem
                      // comparar, cada CPF/CNPJ era consultado 2x (SerPro).
                      setPromissoryBuyerCPForCNPJ((prev) =>
                        prev.join(',') === event.join(',') ? prev : event
                      )
                      onChange(event)
                    }}
                    errorMessage={(errors.buyers as any)?.message}
                  />
                )
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <FormWizardStyles.ButtonsWrapperSpaced>
              <Button variant="link" onClick={backHandler}>
                Voltar
              </Button>
              <Button variant="primary" onClick={handleSubmit(onClickNext)}>
                Próximo
              </Button>
            </FormWizardStyles.ButtonsWrapperSpaced>
          </Col>
        </Row>
      </FormWizardStyles.TabContent>
    </div>
  )
}
